import React, {
  createContext,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import CoralogixLogger from "./coralogix";

interface User {
  id?: string;
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
}

interface CoralogixContextType {
  setUser: (user: User | null) => void;
}

const CoralogixContext = createContext<CoralogixContextType | null>(null);

interface CoralogixProviderProps {
  children: ReactNode;
  user: User | null;
}

// Utility function for direct user context setting (for microcomponents)
export const addCoralogixUserContext = (user: User | null): void => {
  if (user) {
    CoralogixLogger.addUserContext({
      id: user.id,
      user_id: user.id,
      email: user.emailAddress,
      user_email: user.emailAddress,
      name:
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.firstName || user.lastName || "Unknown",
      user_name:
        user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.firstName || user.lastName || "Unknown",
    });
  }
};

export const CoralogixProvider: React.FC<CoralogixProviderProps> = ({
  children,
  user = undefined,
}) => {
  // Set user context whenever user changes
  useEffect(() => {
    if (user !== undefined) {
      addCoralogixUserContext(user);
    }
  }, [user]);

  const contextValue: CoralogixContextType = useMemo(
    () => ({
      setUser: addCoralogixUserContext,
    }),
    []
  );

  return (
    <CoralogixContext.Provider value={contextValue}>
      {children}
    </CoralogixContext.Provider>
  );
};

export const useCoralogix = (): CoralogixContextType => {
  const context = useContext(CoralogixContext);
  if (!context) {
    throw new Error("useCoralogix must be used within a CoralogixProvider");
  }
  return context;
};
